import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import api from "@/lib/api";
import { ENTITY_COLORS, ENTITY_TEXT } from "@/lib/entityColors";

// Reusable search field with autocomplete (KG entities + popular past questions).
export default function SearchAutocomplete({
  initialValue = "",
  onSubmit,
  placeholder = "Ask anything…",
  icon,
  submitIcon,
  pillClassName = "",
  buttonClassName = "",
  testIdPrefix = "search",
}) {
  const navigate = useNavigate();
  const [value, setValue] = useState(initialValue);
  const [open, setOpen] = useState(false);
  const [sugg, setSugg] = useState({ entities: [], questions: [] });
  const [active, setActive] = useState(-1);
  const wrapRef = useRef(null);

  useEffect(() => setValue(initialValue), [initialValue]);

  // Debounced suggestion fetch (guards against stale out-of-order responses).
  useEffect(() => {
    if (!open) return;
    let ignore = false;
    const t = setTimeout(async () => {
      try {
        const res = await api.get("/kg/suggest", { params: { q: value.trim() } });
        if (!ignore) setSugg(res.data || { entities: [], questions: [] });
      } catch {
        if (!ignore) setSugg({ entities: [], questions: [] });
      }
    }, 160);
    return () => { ignore = true; clearTimeout(t); };
  }, [value, open]);

  // Close on outside click.
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const submit = (q) => {
    const query = (q ?? value).trim();
    if (!query) return;
    setOpen(false);
    if (onSubmit) onSubmit(query);
    else navigate(`/answers?q=${encodeURIComponent(query)}`);
  };

  const pickEntity = (ent) => {
    setOpen(false);
    setValue(ent.label);
    navigate(`/explore?focus=${encodeURIComponent(ent.id)}`);
  };

  const flat = [
    ...sugg.questions.map((q) => ({ kind: "q", value: q })),
    ...sugg.entities.map((e) => ({ kind: "e", value: e })),
  ];

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      if (open && active >= 0 && flat[active]) {
        const item = flat[active];
        item.kind === "q" ? submit(item.value) : pickEntity(item.value);
      } else submit();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActive((a) => Math.min(a + 1, flat.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, -1));
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const showDropdown = open && flat.length > 0;

  return (
    <div ref={wrapRef} className="relative">
      <div className={pillClassName}>
        {icon}
        <input
          type="text"
          value={value}
          placeholder={placeholder}
          onChange={(e) => { setValue(e.target.value); setOpen(true); setActive(-1); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          data-testid={`${testIdPrefix}-input`}
          aria-label="Ask a question"
          autoComplete="off"
          className="flex-1 bg-transparent outline-none text-[15px] text-[#14140f] placeholder:text-[#14140f]/35"
        />
        <button
          onClick={() => submit()}
          data-testid={`${testIdPrefix}-submit`}
          aria-label="Search"
          className={buttonClassName}
        >
          {submitIcon}
        </button>
      </div>

      {showDropdown && (
        <div
          className="absolute left-0 right-0 mt-2 bg-white rounded-2xl shadow-xl border border-[#14140f]/10 py-2 z-40 overflow-hidden text-left"
          data-testid="search-suggestions"
        >
          {sugg.questions.length > 0 && (
            <div>
              <div className="px-4 pt-1 pb-1 text-[10px] uppercase tracking-wide text-[#14140f]/40 font-semibold">Questions</div>
              {sugg.questions.map((q, i) => (
                <button
                  key={`q-${q}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => submit(q)}
                  onMouseEnter={() => setActive(i)}
                  data-testid={`suggestion-question-${i}`}
                  className={`w-full flex items-center gap-2.5 px-4 py-2 text-sm text-[#14140f]/85 ${active === i ? "bg-[#f5f4f0]" : "hover:bg-[#f5f4f0]"}`}
                >
                  <Search className="w-3.5 h-3.5 text-[#14140f]/35 shrink-0" />
                  <span className="truncate">{q}</span>
                </button>
              ))}
            </div>
          )}
          {sugg.entities.length > 0 && (
            <div>
              <div className="px-4 pt-2 pb-1 text-[10px] uppercase tracking-wide text-[#14140f]/40 font-semibold">In the graph</div>
              {sugg.entities.map((ent, i) => (
                <button
                  key={ent.id}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pickEntity(ent)}
                  onMouseEnter={() => setActive(sugg.questions.length + i)}
                  data-testid={`suggestion-entity-${ent.id}`}
                  className={`w-full flex items-center gap-2.5 px-4 py-2 text-sm ${active === sugg.questions.length + i ? "bg-[#f5f4f0]" : "hover:bg-[#f5f4f0]"}`}
                >
                  <span
                    className="text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full shrink-0"
                    style={{ background: ENTITY_COLORS[ent.type], color: ENTITY_TEXT[ent.type] }}
                  >
                    {ent.type}
                  </span>
                  <span className="font-medium text-[#14140f] truncate">{ent.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
